import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Heart, ShoppingBag, ChevronDown, Menu, X, User, Shield } from 'lucide-react';
import { ShopifyCartSidebar } from '@/components/ShopifyCartSidebar';
import { useShopifyCart } from '@/hooks/useShopifyCart';
import { useAuth } from '@/components/AuthContext';
import { useUserRole } from '@/hooks';
import { useLocale, type LocaleStrings } from '@/i18n';

interface NavSub {
  id: string;
  href: string;
}
interface NavCat {
  id: string;
  href: string;
  subcategories: NavSub[];
}

const categories: NavCat[] = [
  {
    id: 'apparel',
    href: '/collections/apparel',
    subcategories: [
      { id: 'tops', href: '/collections/tops' },
      { id: 'bottoms', href: '/collections/bottoms' },
      { id: 'outfit-sets', href: '/collections/apparel?filter=outfit-sets' },
      { id: 'sweaters', href: '/collections/apparel?filter=sweaters' },
      { id: 'jumpsuits', href: '/collections/one-pieces?filter=jumpsuits' },
      { id: 'rompers', href: '/collections/one-pieces?filter=rompers' },
    ],
  },
  {
    id: 'dresses',
    href: '/collections/dresses',
    subcategories: [
      { id: 'maxi-dresses', href: '/collections/dresses?filter=maxi' },
      { id: 'midi-dresses', href: '/collections/dresses?filter=midi' },
      { id: 'mini-dresses', href: '/collections/dresses?filter=mini' },
    ],
  },
  {
    id: 'footwear',
    href: '/collections/footwear',
    subcategories: [
      { id: 'sandals', href: '/collections/footwear?filter=sandals' },
      { id: 'boots', href: '/collections/footwear?filter=boots' },
      { id: 'heels', href: '/collections/footwear?filter=heels' },
      { id: 'hot-chocolate-design', href: '/collections/hot-chocolate-design' },
    ],
  },
  {
    id: 'jewelry',
    href: '/collections/jewelry',
    subcategories: [
      { id: 'necklaces', href: '/collections/jewelry?filter=necklaces' },
      { id: 'earrings', href: '/collections/jewelry?filter=earrings' },
      { id: 'bracelets', href: '/collections/jewelry?filter=bracelets' },
      { id: 'rings', href: '/collections/jewelry?filter=rings' },
    ],
  },
  { id: 'goddess', href: '/collections/goddess', subcategories: [] },
  { id: 'lifestyle', href: '/collections/lifestyle-decor', subcategories: [] },
];

const labelFor = (t: LocaleStrings, id: string) => t.megaMenu.taxonomy[id] ?? id;

const PreviewNav = () => {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const { getTotalItems } = useShopifyCart();
  const totalItems = getTotalItems();
  const { user } = useAuth();
  const { isAdmin } = useUserRole();
  const userIsAdmin = isAdmin();
  const t = useLocale();

  const toggleMobileCategory = (name: string) => {
    setMobileExpanded(mobileExpanded === name ? null : name);
  };

  return (
    <nav className="bg-[hsl(30,25%,97%)] border-b border-border sticky top-0 z-50">
      <div className="px-4 sm:px-6 lg:px-10">
        <div className="flex items-center justify-between h-14 lg:h-16">
          {/* Hamburger — mobile only */}
          <button
            className="lg:hidden p-2 -ml-2 text-foreground"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </button>

          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <span className="font-boho text-xl lg:text-2xl font-bold text-foreground">
              Boho Style Co.
            </span>
          </Link>

          {/* Center nav links — desktop */}
          <div className="hidden lg:flex items-center space-x-1">
            {categories.map((cat) => (
              <div
                key={cat.id}
                className="relative"
                onMouseEnter={() => setOpenDropdown(cat.id)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <Link
                  to={cat.href}
                  className="flex items-center gap-1 px-4 py-2 text-sm font-medium tracking-wide uppercase text-foreground hover:text-berry transition-colors"
                >
                  {labelFor(t, cat.id)}
                  {cat.subcategories.length > 0 && (
                    <ChevronDown className="h-3 w-3" />
                  )}
                </Link>

                {openDropdown === cat.id && cat.subcategories.length > 0 && (
                  <div className="absolute top-full left-0 bg-white shadow-lg border border-border rounded-b-lg min-w-[200px] py-2 z-50">
                    {cat.subcategories.map((sub) => (
                      <Link
                        key={sub.id}
                        to={sub.href}
                        className="block px-5 py-2 text-sm text-foreground hover:bg-muted hover:text-berry transition-colors"
                      >
                        {labelFor(t, sub.id)}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Right icons */}
          <div className="flex items-center space-x-3 lg:space-x-4">
            <button className="text-foreground hover:text-berry transition-colors" aria-label="Search">
              <Search className="h-5 w-5" />
            </button>
            <button className="hidden sm:block text-foreground hover:text-berry transition-colors" aria-label="Wishlist">
              <Heart className="h-5 w-5" />
            </button>
            <Link
              to={user ? '/account' : '/auth'}
              className="text-foreground hover:text-berry transition-colors"
              aria-label={user ? 'My account' : 'Sign in'}
            >
              <User className="h-5 w-5" />
            </Link>
            {userIsAdmin && (
              <Link
                to="/admin"
                className="hidden sm:block text-foreground hover:text-berry transition-colors"
                aria-label="Admin dashboard"
                title="Admin"
              >
                <Shield className="h-5 w-5" />
              </Link>
            )}
            <ShopifyCartSidebar showBadgeOnScroll={false}>
              <button className="relative text-foreground hover:text-berry transition-colors" aria-label="Shopping bag">
                <ShoppingBag className="h-5 w-5" />
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-1 bg-berry text-berry-foreground text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center" aria-hidden="true">
                    {totalItems}
                  </span>
                )}
              </button>
            </ShopifyCartSidebar>
          </div>
        </div>
      </div>

      {/* Mobile slide-out menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
          />
          {/* Drawer */}
          <div className="absolute inset-y-0 left-0 w-[300px] max-w-[85vw] bg-background shadow-xl flex flex-col animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between px-4 h-14 border-b border-border">
              <span className="font-boho text-xl font-bold text-foreground">
                Boho Style Co.
              </span>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-2 -mr-2 text-foreground"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Nav links */}
            <div className="flex-1 overflow-y-auto py-4">
              {categories.map((cat) => {
                const catLabel = labelFor(t, cat.id);
                return (
                  <div key={cat.id}>
                    <div className="flex items-center justify-between">
                      <Link
                        to={cat.href}
                        onClick={() => setMobileOpen(false)}
                        className="flex-1 px-6 py-3 text-base font-medium text-foreground active:bg-muted"
                      >
                        {catLabel}
                      </Link>
                      {cat.subcategories.length > 0 && (
                        <button
                          onClick={() => toggleMobileCategory(cat.id)}
                          className="px-4 py-3 text-muted-foreground"
                          aria-label={`Expand ${catLabel}`}
                        >
                          <ChevronDown
                            className={`h-4 w-4 transition-transform ${
                              mobileExpanded === cat.id ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                      )}
                    </div>
                    {mobileExpanded === cat.id && cat.subcategories.length > 0 && (
                      <div className="bg-muted/50">
                        {cat.subcategories.map((sub) => (
                          <Link
                            key={sub.id}
                            to={sub.href}
                            onClick={() => setMobileOpen(false)}
                            className="block px-10 py-2.5 text-sm text-muted-foreground active:text-berry"
                          >
                            {labelFor(t, sub.id)}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Bottom actions */}
            <div className="border-t border-border px-6 py-4 flex flex-wrap items-center gap-x-6 gap-y-3">
              <Link to="/favorites" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 text-sm text-muted-foreground">
                <Heart className="h-5 w-5" /> Favorites
              </Link>
              <Link to={user ? '/account' : '/auth'} onClick={() => setMobileOpen(false)} className="flex items-center gap-2 text-sm text-muted-foreground">
                <User className="h-5 w-5" /> {user ? 'Account' : 'Sign In'}
              </Link>
              {userIsAdmin && (
                <Link to="/admin" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Shield className="h-5 w-5" /> Admin
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default PreviewNav;
